import { useMemo } from "react";

import { Box, BoxOwner } from "../../../../dtos/box";
import { useAuth } from "../../../../features/auth/context";
import { BoxFeature, BoxFeatureSetup } from "./types";

export const ENABLED_FEATURE: BoxFeature = {
    enabled: true,
    reason: "",
};

const DISABLED_FEATURE_NO_BOX: BoxFeature = {
    enabled: false,
    reason: "Box is not loaded",
};

const DISABLED_FEATURE_INSUFFICIENT_PERMISSIONS: BoxFeature = {
    enabled: false,
    reason: "Insufficient permissions to modify",
};

export function useBoxFeatures(box: Box | null, boxOwners: BoxOwner[]): BoxFeatureSetup {
    const {
        isGlobalAdmin,
        isSiteAdmin,
        isSiteEmployee,
        isReadOnlyUser,
    } = useAuth();

    const bankBoxToggle: BoxFeature = useMemo(() => {
        if (!box) return DISABLED_FEATURE_NO_BOX;
        if (!isSiteAdmin && !isGlobalAdmin) {
            return DISABLED_FEATURE_INSUFFICIENT_PERMISSIONS;
        }

        if (boxOwners.length !== 0) {
            return {
                enabled: false,
                reason: "Can be modified if the Box doesn't have owners",
            };
        }

        return ENABLED_FEATURE;
    }, [isSiteAdmin, isGlobalAdmin, boxOwners, box]);

    const customerRelatedFeature: BoxFeature = useMemo(() => {
        if (!box) return DISABLED_FEATURE_NO_BOX;
        if (box.isBankBox) {
            return {
                enabled: false,
                reason: "Bank box can have only employees as owners"
            };
        }

        if (isReadOnlyUser) {
            return DISABLED_FEATURE_INSUFFICIENT_PERMISSIONS;
        }

        return ENABLED_FEATURE;
    }, [box, isReadOnlyUser]);

    const commonFeature: BoxFeature = useMemo(() => {
        if (!box) return DISABLED_FEATURE_NO_BOX;

        if (box.isBankBox && !isSiteAdmin && !isGlobalAdmin) {
            return DISABLED_FEATURE_INSUFFICIENT_PERMISSIONS;
        }

        if (isReadOnlyUser) {
            return DISABLED_FEATURE_INSUFFICIENT_PERMISSIONS;
        }

        return ENABLED_FEATURE;
    }, [isSiteAdmin, isGlobalAdmin, isReadOnlyUser, box]);

    const ownerEnrollFeature: BoxFeature = useMemo(() => {
        if (!box) return DISABLED_FEATURE_NO_BOX;

        if (box.isBankBox && isSiteEmployee) {
            return DISABLED_FEATURE_INSUFFICIENT_PERMISSIONS;
        }

        if (isReadOnlyUser) {
            return DISABLED_FEATURE_INSUFFICIENT_PERMISSIONS;
        }

        return ENABLED_FEATURE;
    }, [isSiteEmployee, isReadOnlyUser, box]);

    return {
        bankBoxToggle: bankBoxToggle,
        orgCustomersLoad: customerRelatedFeature,
        customerRegister: customerRelatedFeature,
        boxOwnerSelect: commonFeature,
        leaseEndDateEdit: commonFeature,
        leaseDelete: commonFeature,
        ownerDelete: commonFeature,
        ownerEnroll: ownerEnrollFeature,
        boxNoteEdit: commonFeature,
    };
}
